import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";

import HomeBodyV2 from "./HomeBodyV2";
import { AuthContext } from "../../Auth/AuthProvider";
import { VerifyPermission } from "../../Funcoes/VerifyPermission";
import useApi from "../../hooks/useApi";
import { useModal } from "../../contexts/ModalContext";
import ModalGerarContratos from "../GerarContratos/ModalGerarContratos";
import ModalSelecionarTermos from "../GerarContratos/ModalSelecionarTermos";

import HomeV2Provider from "./HomeV2Provider";
import HomeV2Nav from "./HomeV2Nav";
import HomeV2Header from "./HomeV2Header";
import { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from "./theme";

const HOVER_OPEN_DELAY_MS = 40;
const HOVER_CLOSE_DELAY_MS = 220;

const NewHomeV2Inner = ({ modulo }) => {
  const { user } = useContext(AuthContext);
  const api = useApi();
  const navigate = useNavigate();
  const { openModal, isModalOpen } = useModal();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingRelat, setLoadingRelat] = useState(false);
  const [colabsParaContrato, setColabsParaContrato] = useState([]);
  const [colabs, setColabs] = useState([]);
  const [colabsFilter, setColabsFilter] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filtersActive, setFiltersActive] = useState(0);

  const [pinnedCollapsed, setPinnedCollapsed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const hoverTimerRef = useRef(null);

  const expanded = !pinnedCollapsed || hovered;
  const drawerWidth = isMobile
    ? 0
    : expanded
      ? DRAWER_WIDTH
      : DRAWER_WIDTH_COLLAPSED;

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const handleHoverChange = useCallback(
    (isHovering) => {
      if (!pinnedCollapsed) {
        clearHoverTimer();
        setHovered(false);
        return;
      }

      clearHoverTimer();
      hoverTimerRef.current = setTimeout(
        () => setHovered(isHovering),
        isHovering ? HOVER_OPEN_DELAY_MS : HOVER_CLOSE_DELAY_MS
      );
    },
    [pinnedCollapsed, clearHoverTimer]
  );

  useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

  useEffect(() => {
    if (!pinnedCollapsed) {
      clearHoverTimer();
      setHovered(false);
    }
  }, [pinnedCollapsed, clearHoverTimer]);

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  const funcoes = useMemo(
    () => [...new Set(colabs.map((item) => item.FUNCAO).filter(Boolean).sort())],
    [colabs]
  );
  const obras = useMemo(
    () => [...new Set(colabs.map((item) => item.CENTRO_CUSTO).filter(Boolean).sort())],
    [colabs]
  );
  const encarregados = useMemo(
    () => [...new Set(colabs.map((item) => item.ENCARREGADO).filter(Boolean).sort())],
    [colabs]
  );
  const contratos = useMemo(
    () => [...new Set(colabs.map((item) => item.CONTRATO).filter(Boolean).sort())],
    [colabs]
  );

  const filterDados = useCallback(
    (filterName, filterMat, filterFunc, filterEnc, filterObra, filterCont, filterStatus, filterMaoObra) => {
      const filtered = colabs.filter((item) => {
        const name_filter =
          !filterName || item.NOME.toUpperCase().includes(filterName.toUpperCase());
        const mat_filter = !filterMat || item.MATRICULA.includes(filterMat);
        const func_filter =
          filterFunc.length === 0 || filterFunc.some((func) => item.FUNCAO.includes(func));
        const enc_filter =
          filterEnc.length === 0 ||
          filterEnc.some((enc) => (item.ENCARREGADO || "").includes(enc));
        const obra_filter =
          filterObra.length === 0 ||
          filterObra.some((obra) => item.CENTRO_CUSTO.includes(obra));
        const cont_filter =
          filterCont.length === 0 ||
          filterCont.some((cont) => (item.CONTRATO || "").includes(cont));
        const stat_filter =
          filterStatus.length === 0 ||
          filterStatus.some((stat) => item.STATUS.includes(stat));
        const mao_filter =
          filterMaoObra.length === 0 ||
          filterMaoObra.some((mao) => item.MAOOBRA.includes(mao));

        return (
          name_filter &&
          mat_filter &&
          func_filter &&
          enc_filter &&
          obra_filter &&
          cont_filter &&
          stat_filter &&
          mao_filter
        );
      });

      setColabsFilter(filtered);
    },
    [colabs]
  );

  const fetchColabs = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setRefreshing(true);
      else setLoading(true);

      try {
        const { data } = await api.get("/Painel/API/GetHomeColabs/");
        setColabs(data);
      } catch (error) {
        console.log(error);
        toast.error("Não foi possível atualizar os colaboradores.");
      } finally {
        if (silent) setRefreshing(false);
        else setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    fetchColabs();
  }, [fetchColabs]);

  const generateRPE = async () => {
    setLoadingRelat(true);

    try {
      const response = await api.post("/Painel/API/GenerateNewRelatorio/", colabsFilter, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "relatorio.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Erro ao gerar o relatório:", error);
      toast.error("Ocorreu um erro ao gerar o relatório. Por favor, tente novamente.");
    } finally {
      setLoadingRelat(false);
    }
  };

  const abrirGerarContratos = () => {
    openModal("modalGerarContratos");
  };

  const continuarParaTermos = (colaboradores) => {
    setColabsParaContrato(colaboradores);
    openModal("modalSelecionarTermos");
  };

  const handleActivate = (item) => {
    if (item.external) {
      window.location.href = item.external;
      return;
    }

    if (item.action) {
      item.action();
      return;
    }

    if (item.path) {
      navigate(item.path);
    }
  };

  const menuItems = useMemo(
    () => [
      {
        label: "AUDITORIA INTERNA",
        path: "/modulo/ssma/auditoria-interna",
        permission: { modulo: "ssma", submodulo: "auditoria-interna", funcao: "access" },
      },
      {
        label: "ATENDIMENTO AMBULAT.",
        path: "/modulo/ssma/atendimento-amb",
        permission: { modulo: "ssma", submodulo: "atendimento-amb", funcao: "access" },
      },
      {
        label: "CHECK-LIST",
        path: "/modulo/ssma/check-list",
        permission: { modulo: "ssma", submodulo: "check-list", funcao: "access" },
      },
      {
        label: "CIPA",
        path: "/modulo/ssma/cipa",
        permission: { modulo: "ssma", submodulo: "cipa", funcao: "access" },
      },
      {
        label: "DDS",
        path: "/modulo/ssma/dds",
        permission: { modulo: "ssma", submodulo: "dds", funcao: "access" },
      },
      {
        label: "DESVIOS",
        path: "/modulo/ssma/desvios",
        permission: { modulo: "ssma", submodulo: "desvios", funcao: "access" },
      },
      {
        label: "EPI-CONTROLE",
        path: "/modulo/ssma/epi",
        permission: { modulo: "ssma", submodulo: "epi", funcao: "access" },
      },
      {
        label: "INSPEÇÃO DE SST",
        external: "https://sistemaintegrado.palmont.com.br/Painel/Inspecao/",
        permission: { modulo: "ssma", submodulo: "inspecao-sst", funcao: "access" },
      },
      {
        label: "INSPEÇÃO AMBIENTAL",
        external: "https://sistemaintegrado.palmont.com.br/Painel/InspecaoMA/",
        permission: { modulo: "ssma", submodulo: "inspecao-ambiental", funcao: "access" },
      },
      {
        label: "PAINEL DE CONTROLE",
        path: "/modulo/ssma/painel-ctrl",
        permission: { modulo: "ssma", submodulo: "painel-ctrl", funcao: "access" },
      },
      {
        label: "RAC",
        path: "/modulo/ssma/rac",
        permission: { modulo: "ssma", submodulo: "rac", funcao: "access" },
      },
      {
        label: "TREINAMENTOS",
        path: "/modulo/ssma/treinamentos",
        permission: { modulo: "ssma", submodulo: "treinamentos", funcao: "access" },
      },
      {
        label: "AFASTAMENTOS",
        path: "/modulo/rh/afastamentos",
        permission: { modulo: "rh", submodulo: "afastamentos", funcao: "access" },
      },
      {
        label: "ATENDIMENTOS",
        external: "https://sistemaintegrado.palmont.com.br/Painel/Atendimento/",
        permission: { modulo: "rh", submodulo: "atendimentos", funcao: "access" },
      },
      {
        label: "CONTROLE DE EXPERIÊNCIA",
        path: "/modulo/rh/controle-exp",
        permission: { modulo: "rh", submodulo: "controle-exp", funcao: "access" },
      },
      {
        label: "EXAMES OCUPACIONAIS",
        path: "/modulo/rh/exames-ocupa",
        permission: { modulo: "rh", submodulo: "exames-ocupa", funcao: "access" },
      },
      {
        label: "GER. FOLGAS",
        path: "/modulo/rh/ger-folgas",
        permission: { modulo: "rh", submodulo: "ger-folgas", funcao: "access" },
      },
      {
        label: "FOLHA DE PAGAMENTO",
        path: "/modulo/rh/folha-pag",
        permission: { modulo: "rh", submodulo: "folha-pag", funcao: "access" },
      },
      {
        label: "PROCESSO ADM/TRANSF",
        path: "/modulo/rh/processo-adm",
        permission: { modulo: "rh", submodulo: "processo-adm", funcao: "access" },
      },
      {
        label: "REPUBLICAS",
        path: "/modulo/rh/republicas",
        permission: { modulo: "rh", submodulo: "republicas", funcao: "access" },
      },
      {
        label: "GERAR RELATÓRIO",
        action: generateRPE,
        isLoading: loadingRelat,
      },
      {
        label: "GERAR CONTRATOS",
        action: abrirGerarContratos,
        permission: { modulo: "rh" },
      },
      {
        label: "ORGANOGRAMA COLABS",
        path: "/modulo/rh/organograma-colabs",
        permission: { modulo: "rh", submodulo: "organograma", funcao: "access" },
      },
      {
        label: "ORGANOGRAMA COLABS",
        path: "/modulo/ssma/organograma-colabs",
        permission: { modulo: "ssma", submodulo: "organograma", funcao: "access" },
      },
      {
        label: "ORGANOGRAMA COLABS",
        path: "/modulo/qualidade/organograma-colabs",
        permission: { modulo: "qualidade", submodulo: "organograma", funcao: "access" },
      },
      {
        label: "CALIB. EQUIPAMENTOS",
        path: "/modulo/qualidade/equip-calibragem",
        permission: { modulo: "qualidade", submodulo: "equip-calibragem", funcao: "access" },
      },
    ],
    // generateRPE / abrirGerarContratos dependem de estado atual
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingRelat, colabsFilter]
  );

  const orderedMenuItems = useMemo(() => {
    return menuItems
      .filter((item) => {
        if (!item.permission) return true;
        const sameModulo = item.permission.modulo === modulo;
        const hasPermission = VerifyPermission(user, item.permission);
        return sameModulo && hasPermission;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [menuItems, modulo, user]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100dvh",
        bgcolor: "background.default",
        color: "text.primary",
        overflow: "hidden",
      }}
    >
      <HomeV2Nav
        modulo={modulo}
        items={orderedMenuItems}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        expanded={expanded}
        pinnedCollapsed={pinnedCollapsed}
        onToggleCollapse={() => setPinnedCollapsed((value) => !value)}
        onHoverChange={handleHoverChange}
        onActivate={handleActivate}
      />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
          transition: "width 280ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <HomeV2Header
          modulo={modulo}
          count={colabsFilter.length}
          onMenuClick={isMobile ? () => setMobileOpen(true) : undefined}
          onRefresh={() => fetchColabs({ silent: true })}
          onFilter={() => setFilterOpen(true)}
          refreshing={refreshing}
          filtersActive={filtersActive}
        />

        <Box
          sx={(themeArg) => ({
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.default",
            backgroundImage:
              themeArg.palette.mode === "dark"
                ? "radial-gradient(ellipse at top right, rgba(91,163,188,0.08), transparent 55%)"
                : "radial-gradient(ellipse at top right, rgba(232,163,23,0.08), transparent 55%)",
          })}
        >
          <HomeBodyV2
            dados={colabsFilter}
            funcoes={funcoes}
            obras={obras}
            encarregados={encarregados}
            contratos={contratos}
            onFilter={filterDados}
            loading={loading}
            modulo={modulo}
            filterOpen={filterOpen}
            onFilterClose={() => setFilterOpen(false)}
            onFiltersChange={setFiltersActive}
          />
        </Box>
      </Box>

      {isModalOpen("modalGerarContratos") && (
        <ModalGerarContratos colabs={colabs} onContinuar={continuarParaTermos} />
      )}
      {isModalOpen("modalSelecionarTermos") && (
        <ModalSelecionarTermos colaboradores={colabsParaContrato} />
      )}

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        style={{ zIndex: "99999" }}
      />
    </Box>
  );
};

const NewHomeV2 = ({ modulo }) => (
  <HomeV2Provider>
    <NewHomeV2Inner modulo={modulo} />
  </HomeV2Provider>
);

export default NewHomeV2;
